import React, { useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import CustomFileUploader from "../../components/common/CustomFileUploader";
import MultiSelected from "../../components/common/MultiSelected";
import HighLightCreate from "../../components/common/HighLightCreate";
import staticData from "../../utils/staticData";
import extractIframeSrc from "../../utils/extractMapSrc";
import convertPrice from "../../utils/convertAmount";
import ProjectCategoriesProvider from "../../components/common/ProjectCategoriesProvider";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Divider,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import { FiMapPin, FiDollarSign, FiHome } from "react-icons/fi";

import "./AddProperty.css";
import apis from "../../utils/apis";
import httpAction from "../../utils/httpAction";
import toast from "react-hot-toast";
import useGeneral from "../../hooks/useGeneral";
const TYPE_TABS = ["residential", "plot", "commercial", "project"];

const AddProperty = () => {
  const [editing, setEditing] = useState({});
  const [locations, setLocations] = useState([]);
  const params = new URLSearchParams(window.location.search);
  const id = params.get("id");
  const getSingleProp = async () => {
    const data = {
      url: apis().singleProperty + "?id=" + id,
    };
    const result = await httpAction(data);

    if (result?.status) {
      setEditing({
        data: { ...result?.property?.data, user: result?.property?.user?.name },
      });
    }
  };
  useEffect(() => {
    if (id) {
      getSingleProp();
    }
  }, [id]);

  const initialValues = {
    title: editing?.data?.title || "",
    price: editing?.data?.price || "",
    location: editing?.data?.location || "",
    type: editing?.data?.type || "residential",
    size: editing?.data?.size || "",
    des: editing?.data?.des || "",
    status: editing?.data?.status || "",
    bed: editing?.data?.bed || "",
    bath: editing?.data?.bath || "",
    images: editing?.data?.images || [], // <-- will store array of uploaded urls OR files (your uploader decides)
    ammenities: editing?.data?.ammenities || [],
    city: editing?.data?.city || "",
    user: editing?.data?.user || "",
    towers: editing?.data?.towers || "",
    units: editing?.data?.units || "",
    floors: editing?.data?.floors || "",
    construction: editing?.data?.construction || "",
    categories: editing?.data?.categories || [],
    highlights: editing?.data?.highlights || [],
    mapLink: editing?.data?.mapLink || "",
    booking: editing?.data?.booking || "",
    installment: editing?.data?.installment || "",
    possession: editing?.data?.possession || "",
    confirmation: editing?.data?.confirmation || "",
    completionDate: editing?.data?.completionDate || "",
    constructionProgress: editing?.data?.constructionProgress || "",
  };

  const schema = useMemo(() => {
    return Yup.object({
      title: Yup.string()
        .trim()
        .min(3, "Too short")
        .required("Title is required"),
      city: Yup.string()
        .trim()
        .min(3, "Too short")
        .required("City is required"),
      user: Yup.string()
        .trim()
        .min(3, "Too short")
        .required("User is required"),
      des: Yup.string()
        .trim()
        .min(10, "Too short")
        .required("Description is required"),
      price: Yup.number()
        .typeError("Price must be a number")
        .min(0, "Price cannot be negative")
        .required("Price is required"),
      location: Yup.string().trim().required("Location is required"),
      type: Yup.string().oneOf(TYPE_TABS).required("Type is required"),
      size: Yup.string().trim().required("Size is required"),

      // ✅ Images: min 3
      images: Yup.array()
        .min(3, "At least 3 images are required")
        .required("Images are required"),
      ammenities: Yup.array()
        .min(3, "At least 3 ammenities are required")
        .required("Ammenities are required"),

      // conditional for projects
      highlights: Yup.array().when("type", {
        is: (t) => t === "project",
        then: (s) =>
          s
            .min(3, "At least 3 hight lights are required")
            .required("High lights are required"),
        otherwise: (s) => s.notRequired(),
      }),
      categories: Yup.array().when("type", {
        is: (t) => t === "project",
        then: (s) =>
          s
            .min(3, "At least 3 categories are required")
            .required("categories are required"),
        otherwise: (s) => s.notRequired(),
      }),
      mapLink: Yup.string().when("type", {
        is: (t) => t === "project",
        then: (s) => s.required("Location link is required"),
      }),

      towers: Yup.number().when("type", {
        is: (t) => t === "project",
        then: (s) =>
          s
            .typeError("must be a number")
            .integer("must be a whole number")
            .min(0, "Invalid value")
            .required("Number of towers are required"),
        otherwise: (s) => s.notRequired(),
      }),
      floors: Yup.number().when("type", {
        is: (t) => t === "project",
        then: (s) =>
          s
            .typeError("must be a number")
            .integer("must be a whole number")
            .min(0, "Invalid value")
            .required("Number of floors are required"),
        otherwise: (s) => s.notRequired(),
      }),
      units: Yup.number().when("type", {
        is: (t) => t === "project",
        then: (s) =>
          s
            .typeError("must be a number")
            .integer("must be a whole number")
            .min(0, "Invalid value")
            .required("Number of units are required"),
        otherwise: (s) => s.notRequired(),
      }),
      completionDate: Yup.string().when("type", {
        is: (t) => t === "project",
        then: (s) => s.required("Completion Date is required"),
        otherwise: (s) => s.notRequired(),
      }),
      constructionProgress: Yup.number().when("type", {
        is: (t) => t === "project",
        then: (s) =>
          s
            .typeError("must be a number")
            .integer("must be a whole number")
            .min(1, "Invalid value")
            .required("Progress is required"),
        otherwise: (s) => s.notRequired(),
      }),

      construction: Yup.string().when("type", {
        is: (t) => t === "project",
        then: (s) => s.required("Construction status is required"),
        otherwise: (s) => s.notRequired(),
      }),

      booking: Yup.number().when("type", {
        is: (t) => t === "project",
        then: (s) =>
          s
            .typeError("must be a number")
            .integer("must be a whole number")
            .min(1, "Invalid value")
            .required("Booking is required"),
        otherwise: (s) => s.notRequired(),
      }),
      installment: Yup.number().when("type", {
        is: (t) => t === "project",
        then: (s) =>
          s
            .typeError("must be a number")
            .integer("must be a whole number")
            .min(1, "Invalid value")
            .required("Installment is required"),
        otherwise: (s) => s.notRequired(),
      }),
      possession: Yup.number().when("type", {
        is: (t) => t === "project",
        then: (s) =>
          s
            .typeError("must be a number")
            .integer("must be a whole number")
            .min(1, "Invalid value")
            .required("Possession is required"),
        otherwise: (s) => s.notRequired(),
      }),

      confirmation: Yup.number().when("type", {
        is: (t) => t === "project",
        then: (s) =>
          s
            .typeError("must be a number")
            .integer("must be a whole number")
            .min(1, "Invalid value")
            .required("Confirmation is required"),
        otherwise: (s) => s.notRequired(),
      }),

      // ✅ Conditional: only for apartment/house
      bed: Yup.number().when("type", {
        is: (t) => t === "residential" || t === "apartment",
        then: (s) =>
          s
            .typeError("Bed must be a number")
            .integer("Bed must be a whole number")
            .min(0, "Invalid value")
            .required("Bed is required"),
        otherwise: (s) => s.notRequired(),
      }),

      bath: Yup.number().when("type", {
        is: (t) => t === "residential" || t === "apartment",
        then: (s) =>
          s
            .typeError("Bath must be a number")
            .integer("Bath must be a whole number")
            .min(0, "Invalid value")
            .required("Bath is required"),
        otherwise: (s) => s.notRequired(),
      }),
      status: Yup.string().when("type", {
        is: (t) => t === "residential" || t === "apartment",
        then: (s) => s.required("Status is required"),
        otherwise: (s) => s.notRequired(),
      }),
    });
  }, []);
  const { loading, setLoading, navigate } = useGeneral();
  const [users, setUsers] = useState([]);

  const getUsers = async () => {
    const data = {
      url: apis().getUserList,
    };
    const result = await httpAction(data);
    if (result?.status) {
      const name = result?.list?.map((item) => item.name);

      setUsers(name);
    }
  };

  const getLocations = async () => {
    const data = {
      url: apis().getLocationList,
    };
    const result = await httpAction(data);
    if (result?.status) {
      const list = result?.list?.map((item) => item?.title);
      setLocations(list);
    }
  };

  useEffect(() => {
    getUsers();
    getLocations();
  }, []);

  const saveHandler = async (values) => {
    const formatedMapSrc = extractIframeSrc(values?.mapLink);

    const data = {
      url: editing?.data?.title
        ? apis().editProperty + "?id=" + id
        : apis().addProperty,
      method: editing?.data?.title ? "PUT" : "POST",
      body: { data: { ...values, mapLink: formatedMapSrc } },
    };

    const result = await httpAction(data);
    if (result?.status) {
      toast.success(result?.message);
      navigate("/admin/properties");
    }
  };

  return (
    <Box className="addPropertyPage">
      <Paper className="addPropertyCard" elevation={0}>
        <Box className="addPropertyHeader">
          <Box>
            <Typography className="addPropertyTitle">Add Property</Typography>
            <Typography className="addPropertySub">
              Fill the details carefully. Minimum 3 images are required.
            </Typography>
          </Box>
        </Box>

        <Divider className="divider" />

        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={schema}
          onSubmit={saveHandler}
        >
          {(formik) => {
            const isHomeType =
              formik.values.type === "residential" ||
              formik.values.type === "apartment";

            const tabIndex = Math.max(
              0,
              TYPE_TABS.findIndex((t) => t === formik.values.type)
            );

            return (
              <form onSubmit={formik.handleSubmit}>
                {/* TYPE TABS */}
                <Box className="typeTabsWrap">
                  <Typography className="sectionLabel">
                    Property Type
                  </Typography>

                  <Tabs
                    value={tabIndex}
                    onChange={(_, idx) => {
                      const t = TYPE_TABS[idx];
                      formik.setFieldValue("type", t);

                      // optional: clear bed/bath when switching to non-home types
                      if (t === "plot" || t === "commercial") {
                        formik.setFieldValue("bed", "");
                        formik.setFieldValue("bath", "");
                      }
                    }}
                    className="typeTabs"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {TYPE_TABS.map((t) => (
                      <Tab
                        key={t}
                        label={t.toUpperCase()}
                        className="typeTab"
                      />
                    ))}
                  </Tabs>

                  {formik.touched.type && formik.errors.type ? (
                    <div className="fieldError">{formik.errors.type}</div>
                  ) : null}
                </Box>

                {/* GRID */}
                <Box className="formGrid">
                  <Box className="field">
                    <Typography className="label">Title</Typography>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="e.g. 10 Marla Modern House"
                      {...formik.getFieldProps("title")}
                      error={Boolean(
                        formik.touched.title && formik.errors.title
                      )}
                      helperText={formik.touched.title && formik.errors.title}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FiHome />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box sx={{ position: "relative" }} className="field">
                    <Typography className="label">Price</Typography>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="e.g. 25000000"
                      {...formik.getFieldProps("price")}
                      error={Boolean(
                        formik.touched.price && formik.errors.price
                      )}
                      helperText={formik.touched.price && formik.errors.price}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FiDollarSign />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {formik.values.price && (
                      <span
                        style={{
                          position: "absolute",
                          top: "-4px",
                          left: "44px",
                        }}
                      >
                        {convertPrice(formik.values.price)}
                      </span>
                    )}
                  </Box>

                  <Box className="field">
                    <Typography className="label">Select location</Typography>
                    <Autocomplete
                      options={locations}
                      onChange={(e, value) =>
                        formik.setFieldValue("location", value)
                      }
                      value={formik.values.location}
                      renderInput={(param) => (
                        <TextField
                          {...param}
                          size="small"
                          fullWidth
                          label="Location"
                          {...formik.getFieldProps("location")}
                          error={Boolean(
                            formik.touched.location && formik.errors.location
                          )}
                          helperText={
                            formik.touched.location && formik.errors.location
                          }
                          // InputProps={{
                          //   startAdornment: (
                          //     <InputAdornment position="start">
                          //       <FiMapPin />
                          //     </InputAdornment>
                          //   ),
                          // }}
                        />
                      )}
                    />
                  </Box>

                  <Box className="field">
                    <Typography className="label">Size</Typography>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="e.g. 10 Marla / 1 Kanal / 5 Marla"
                      {...formik.getFieldProps("size")}
                      error={Boolean(formik.touched.size && formik.errors.size)}
                      helperText={formik.touched.size && formik.errors.size}
                    />
                  </Box>

                  {/* ✅ SHOW ONLY FOR house/apartment */}
                  {isHomeType && (
                    <>
                      <div className="row">
                        <div className="col-md-6">
                          <Box className="field">
                            <Typography className="label">Bedrooms</Typography>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="e.g. 4"
                              {...formik.getFieldProps("bed")}
                              error={Boolean(
                                formik.touched.bed && formik.errors.bed
                              )}
                              helperText={
                                formik.touched.bed && formik.errors.bed
                              }
                            />
                          </Box>
                        </div>
                        <div className="col-md-6">
                          <Box className="field">
                            <Typography className="label">Bathrooms</Typography>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="e.g. 5"
                              {...formik.getFieldProps("bath")}
                              error={Boolean(
                                formik.touched.bath && formik.errors.bath
                              )}
                              helperText={
                                formik.touched.bath && formik.errors.bath
                              }
                            />
                          </Box>
                        </div>
                      </div>

                      <Box className="field">
                        <Typography className="label">Select Status</Typography>
                        <Autocomplete
                          size="small"
                          onChange={(e, value) =>
                            formik.setFieldValue("status", value)
                          }
                          value={formik.values.status}
                          options={["new", "used"]}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              {...params}
                              fullWidth
                              placeholder="e.g. new or used"
                              // {...formik.getFieldProps("status")}
                              onBlur={formik.handleBlur}
                              error={Boolean(
                                formik.touched.status && formik.errors.status
                              )}
                              helperText={
                                formik.touched.status && formik.errors.status
                              }
                            />
                          )}
                        />
                      </Box>
                    </>
                  )}

                  <div className="col-md-12">
                    <Typography className="label">Select City</Typography>
                    <Autocomplete
                      options={staticData().pakistanMajorCities}
                      value={formik.values.city}
                      onChange={(e, value) =>
                        formik.setFieldValue("city", value)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onBlur={formik.handleBlur}
                          name="city"
                          size="small"
                          fullWidth
                          placeholder="e.g. lahore "
                          error={Boolean(
                            formik.touched.city && formik.errors.city
                          )}
                          helperText={formik.touched.city && formik.errors.city}
                        />
                      )}
                    />
                  </div>

                  <div className="col-md-12">
                    <Typography className="label">Select User</Typography>
                    <Autocomplete
                      options={users}
                      value={formik.values.user}
                      onChange={(e, value) =>
                        formik.setFieldValue("user", value)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onBlur={formik.handleBlur}
                          name="user"
                          size="small"
                          fullWidth
                          placeholder="e.g. user "
                          error={Boolean(
                            formik.touched.user && formik.errors.user
                          )}
                          helperText={formik.touched.user && formik.errors.user}
                        />
                      )}
                    />
                  </div>

                  {/* show only for projects */}

                  {formik.values.type === "project" && (
                    <>
                      <Box className="field fieldFull">
                        <Typography className="label">
                          Map Location Link
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g. https://"
                          {...formik.getFieldProps("mapLink")}
                          error={Boolean(
                            formik.touched.mapLink && formik.errors.mapLink
                          )}
                          helperText={
                            formik.touched.mapLink && formik.errors.mapLink
                          }
                        />
                      </Box>
                      <div className="row">
                        <div className="col-md-6">
                          <Typography className="label">
                            Number of towers
                          </Typography>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="e.g. 1 2 3"
                            {...formik.getFieldProps("towers")}
                            error={Boolean(
                              formik.touched.towers && formik.errors.towers
                            )}
                            helperText={
                              formik.touched.towers && formik.errors.towers
                            }
                          />
                        </div>
                        <div className="col-md-6">
                          <Typography className="label">
                            Number of units
                          </Typography>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="e.g. 20 50 "
                            {...formik.getFieldProps("units")}
                            error={Boolean(
                              formik.touched.units && formik.errors.units
                            )}
                            helperText={
                              formik.touched.units && formik.errors.units
                            }
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <Typography className="label">
                            Number of floors
                          </Typography>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="e.g. 7 10"
                            {...formik.getFieldProps("floors")}
                            error={Boolean(
                              formik.touched.floors && formik.errors.floors
                            )}
                            helperText={
                              formik.touched.floors && formik.errors.floors
                            }
                          />
                        </div>
                        <div className="col-md-6">
                          <Typography className="label">
                            Construction Status
                          </Typography>
                          <Autocomplete
                            options={[
                              "pre-launch",
                              "under construction",
                              "ready",
                            ]}
                            value={formik.values.construction}
                            onChange={(e, value) =>
                              formik.setFieldValue("construction", value)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                onBlur={formik.handleBlur}
                                name="construction"
                                size="small"
                                fullWidth
                                placeholder="e.g. pre launch"
                                error={Boolean(
                                  formik.touched.construction &&
                                    formik.errors.construction
                                )}
                                helperText={
                                  formik.touched.construction &&
                                  formik.errors.construction
                                }
                              />
                            )}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <Typography className="label">
                            Booking from %
                          </Typography>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="e.g. 10%"
                            {...formik.getFieldProps("booking")}
                            error={Boolean(
                              formik.touched.booking && formik.errors.booking
                            )}
                            helperText={
                              formik.touched.booking && formik.errors.booking
                            }
                          />
                        </div>
                        <div className="col-md-6">
                          <Typography className="label">
                            Monthly installment %
                          </Typography>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="e.g. 6%"
                            {...formik.getFieldProps("installment")}
                            error={Boolean(
                              formik.touched.installment &&
                                formik.errors.installment
                            )}
                            helperText={
                              formik.touched.installment &&
                              formik.errors.installment
                            }
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <Typography className="label">
                            Confirmation %
                          </Typography>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="e.g. 10%"
                            {...formik.getFieldProps("confirmation")}
                            error={Boolean(
                              formik.touched.confirmation &&
                                formik.errors.confirmation
                            )}
                            helperText={
                              formik.touched.confirmation &&
                              formik.errors.confirmation
                            }
                          />
                        </div>
                        <div className="col-md-6">
                          <Typography className="label">Possesion %</Typography>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="e.g. 6%"
                            {...formik.getFieldProps("possession")}
                            error={Boolean(
                              formik.touched.possession &&
                                formik.errors.possession
                            )}
                            helperText={
                              formik.touched.possession &&
                              formik.errors.possession
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-12">
                        <Typography className="label">
                          Construction Progress %
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g. 6"
                          {...formik.getFieldProps("constructionProgress")}
                          error={Boolean(
                            formik.touched.constructionProgress &&
                              formik.errors.constructionProgress
                          )}
                          helperText={
                            formik.touched.constructionProgress &&
                            formik.errors.constructionProgress
                          }
                        />
                      </div>
                      <div className="col-md-12">
                        <Typography className="label">
                          Completion Date
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g. Dec-2026"
                          {...formik.getFieldProps("completionDate")}
                          error={Boolean(
                            formik.touched.completionDate &&
                              formik.errors.completionDate
                          )}
                          helperText={
                            formik.touched.completionDate &&
                            formik.errors.completionDate
                          }
                        />
                      </div>
                      <Box className="field fieldFull">
                        <Typography className="label">High Lights</Typography>
                        <HighLightCreate
                          values={formik.values.highlights}
                          error={Boolean(
                            formik.touched.highlights &&
                              formik.errors.highlights
                          )}
                          helperText={
                            formik.touched.highlights &&
                            formik.errors.highlights
                          }
                          onChange={(item) => {
                            formik.setFieldValue("highlights", item);
                          }}
                        />
                      </Box>
                      <Box className="field fieldFull">
                        <Typography className="label">
                          Select Project Categories
                        </Typography>
                        <ProjectCategoriesProvider
                          values={formik.values.categories}
                          onChange={(e, value) => {
                            formik.setFieldValue("categories", value);
                          }}
                          error={Boolean(
                            formik.touched.categories &&
                              formik.errors.categories
                          )}
                          helperText={
                            formik.touched.categories &&
                            formik.errors.categories
                          }
                          blur={formik.handleBlur}
                        />
                      </Box>
                    </>
                  )}

                  <Box className="field fieldFull">
                    <Typography className="label">Select Ammenities</Typography>
                    <MultiSelected
                      values={formik.values.ammenities}
                      category={formik.values.type}
                      blur={formik.handleBlur}
                      error={Boolean(
                        formik.touched.ammenities && formik.errors.ammenities
                      )}
                      helperText={
                        formik.touched.ammenities && formik.errors.ammenities
                      }
                      onChange={(e, values) => {
                        formik.setFieldValue("ammenities", values);
                      }}
                    />
                  </Box>
                  <Box className="field fieldFull">
                    <Typography className="label">Description</Typography>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={4}
                      placeholder="Write a good description..."
                      {...formik.getFieldProps("des")}
                      error={Boolean(formik.touched.des && formik.errors.des)}
                      helperText={formik.touched.des && formik.errors.des}
                    />
                  </Box>
                  {/* IMAGES */}
                  <Box className="field fieldFull">
                    <Box className="uploaderHead">
                      <Typography className="label">Images</Typography>
                      <Typography className="hint">
                        Upload minimum 3 images.
                      </Typography>
                    </Box>

                    <CustomFileUploader
                      onEdit={formik.values.images}
                      multiple={true}
                      onChange={(arr) => {
                        const list = arr.map((item) => item.url);
                        // IMPORTANT: Your uploader should return array of uploaded URLs
                        // OR array of files (if you want to upload later).
                        // Schema expects array length >= 3 either way.
                        formik.setFieldValue("images", arr);
                        formik.setFieldTouched("images", true, true);
                      }}
                    />

                    {formik.touched.images && formik.errors.images ? (
                      <div className="fieldError">{formik.errors.images}</div>
                    ) : null}
                  </Box>
                </Box>

                <Divider className="divider" />

                <Box className="actionsRow">
                  <Button
                    variant="outlined"
                    type="button"
                    className="btnSecondary"
                    onClick={() => formik.resetForm()}
                  >
                    Reset
                  </Button>

                  <Button
                    variant="contained"
                    type="submit"
                    className="btnPrimary"
                    disabled={formik.isSubmitting}
                  >
                    Save Property
                  </Button>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Paper>
    </Box>
  );
};

export default AddProperty;
